import React from "react";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { CONTEUDO, ATUALIZADA, EMPRESA, CONTATO } from "./politicaConteudo";

/**
 * Página da Política de Privacidade (LGPD — Lei 13.709/2018).
 *
 * Acessível em /privacidade SEM login: titulares de dados (incluindo tomadores
 * de serviço, que nunca criam conta) precisam conseguir ler e exercer direitos.
 *
 * Este arquivo cuida APENAS da apresentação. O texto vive em politicaConteudo.js,
 * que também alimenta a geração do POLITICA_PRIVACIDADE.md.
 */

/** Converte **negrito** e [rótulo](destino) em elementos React. */
function formatar(texto, estiloForte) {
  const partes = [];
  const regex = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  let ultimo = 0, m, chave = 0;

  while ((m = regex.exec(texto)) !== null) {
    if (m.index > ultimo) partes.push(texto.slice(ultimo, m.index));
    const t = m[0];
    if (t.startsWith("**")) {
      partes.push(<strong key={chave++} style={estiloForte}>{t.slice(2, -2)}</strong>);
    } else {
      const [, rotulo, destino] = t.match(/\[([^\]]+)\]\(([^)]+)\)/);
      partes.push(
        <a key={chave++} href={destino} style={{ color: "#0ea5e9", textDecoration: "none" }}>
          {rotulo}
        </a>
      );
    }
    ultimo = regex.lastIndex;
  }
  if (ultimo < texto.length) partes.push(texto.slice(ultimo));
  return partes;
}

export default function PoliticaPrivacidade({ T, onVoltar }) {
  const c = T || {
    bg: "#f0f4f8", card: "#ffffff", text: "#0f172a", textMuted: "#475569",
    textFaint: "#94a3b8", border: "#e2e8f0", cardInner: "#f8fafc", isDark: false,
  };

  const S = {
    h2: { fontSize: 17, fontWeight: 700, color: c.text, margin: "34px 0 12px" },
    h3: { fontSize: 14.5, fontWeight: 700, color: c.text, margin: "22px 0 10px" },
    p:  { fontSize: 14.5, lineHeight: 1.75, color: c.textMuted, margin: "0 0 14px" },
    li: { fontSize: 14.5, lineHeight: 1.75, color: c.textMuted, marginBottom: 7 },
    forte: { color: c.text, fontWeight: 600 },
    th: { textAlign: "left", fontSize: 12, fontWeight: 700, color: c.textFaint,
          padding: "9px 12px", borderBottom: "1px solid " + c.border, letterSpacing: 0.3 },
    td: { fontSize: 13.5, color: c.textMuted, padding: "9px 12px",
          borderBottom: "1px solid " + c.border, lineHeight: 1.6, verticalAlign: "top" },
  };

  const bloco = (b, i) => {
    switch (b.tipo) {
      case "secao":
        return <h2 key={i} style={S.h2}>{b.titulo}</h2>;

      case "subsecao":
        return <h3 key={i} style={S.h3}>{b.titulo}</h3>;

      case "paragrafo":
        return <p key={i} style={S.p}>{formatar(b.texto, S.forte)}</p>;

      case "lista":
        return (
          <ul key={i} style={{ margin: "0 0 14px", paddingLeft: 20 }}>
            {b.itens.map((item, j) => (
              <li key={j} style={S.li}>{formatar(item, S.forte)}</li>
            ))}
          </ul>
        );

      case "tabela":
        return (
          <div key={i} style={{ overflowX: "auto", margin: "0 0 18px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", background: c.cardInner,
                            border: "1px solid " + c.border, borderRadius: 8 }}>
              <thead><tr>{b.cabecalho.map((h, j) => <th key={j} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {b.linhas.map((linha, j) => (
                  <tr key={j}>{linha.map((v, k) => <td key={k} style={S.td}>{v}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case "destaque":
        return (
          <div key={i} style={{ background: c.isDark ? "#0d2030" : "#eff6ff",
                                border: "1px solid " + (c.isDark ? "#3b82f640" : "#bfdbfe"),
                                borderRadius: 9, padding: "13px 16px", margin: "0 0 16px" }}>
            <p style={{ ...S.p, margin: 0, color: c.isDark ? "#93c5fd" : "#1d4ed8", fontWeight: 600 }}>
              {formatar(b.texto, { fontWeight: 700 })}
            </p>
          </div>
        );

      case "aviso":
        return (
          <div key={i} style={{ background: c.isDark ? "#2a1f0d" : "#fffbeb",
                                border: "1px solid " + (c.isDark ? "#f59e0b40" : "#fde68a"),
                                borderRadius: 9, padding: "13px 16px", margin: "0 0 16px" }}>
            <p style={{ ...S.p, margin: 0, fontSize: 13.5,
                        color: c.isDark ? "#fcd34d" : "#92400e" }}>
              {formatar(b.texto, { fontWeight: 700 })}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: c.bg, overflowY: "auto",
                  fontFamily: "'IBM Plex Sans',sans-serif" }}>
      <link href="/fonts/ibm-plex-sans.css" rel="stylesheet" />

      <div style={{ maxWidth: 820, margin: "0 auto", padding: "32px 22px 80px" }}>

        {onVoltar && (
          <button onClick={onVoltar}
            style={{ display: "flex", alignItems: "center", gap: 7, background: "transparent",
                     border: "none", color: "#0ea5e9", fontSize: 13.5, fontWeight: 600,
                     cursor: "pointer", padding: 0, marginBottom: 26,
                     fontFamily: "'IBM Plex Sans',sans-serif" }}>
            <ArrowLeft size={15} /> Voltar
          </button>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 6 }}>
          <div style={{ width: 38, height: 38, borderRadius: 9, flexShrink: 0,
                        background: "linear-gradient(135deg,#0ea5e9,#0284c7)", display: "flex",
                        alignItems: "center", justifyContent: "center" }}>
            <ShieldCheck size={19} style={{ color: "#fff" }} />
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: c.text, margin: 0, lineHeight: 1.25 }}>
            Política de Privacidade
          </h1>
        </div>
        <p style={{ fontSize: 13, color: c.textFaint, margin: "0 0 30px" }}>
          Sistema de Emissão de NFS-e · Última atualização: {ATUALIZADA}
        </p>

        {CONTEUDO.map(bloco)}

        <div style={{ borderTop: "1px solid " + c.border, marginTop: 34, paddingTop: 20 }}>
          <p style={{ fontSize: 13, color: c.textFaint, margin: 0, lineHeight: 1.7 }}>
            <span style={{ fontWeight: 700, color: c.textMuted }}>{EMPRESA}</span><br />
            <a href={"mailto:" + CONTATO} style={{ color: "#0ea5e9", textDecoration: "none" }}>
              {CONTATO}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
